import React, { useEffect, useRef, useState } from 'react'
import {
    Alert,
    Box,
    IconButton,
    Link,
    Paper,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import { useTranslation } from 'react-i18next'
import { AssistantExchange, AssistantThreadSummary } from '@/hooks/useAssistant'
import { renderAssistantMarkdown } from './assistantMarkdown'

const TypingIndicator = () => (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', px: 1, py: 0.5 }}>
        {[0, 1, 2].map((dot) => (
            <Box
                key={dot}
                sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: 'grey.500',
                    animation: 'assistantTyping 1.2s infinite',
                    animationDelay: `${dot * 0.2}s`,
                    '@keyframes assistantTyping': {
                        '0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
                        '30%': { opacity: 1, transform: 'translateY(-3px)' },
                    },
                }}
            />
        ))}
    </Box>
)

type AssistantPanelProps = {
    exchanges: AssistantExchange[]
    asking: boolean
    askFailed: boolean
    conversations: AssistantThreadSummary[] | null
    activeThreadId: number | null
    onAsk: (question: string) => void
    onNewConversation: () => void
    onOpenHistory: () => void
    onOpenThread: (thread: AssistantThreadSummary) => void
    onFeedback: (messageId: number, liked: boolean) => void
    onClose: () => void
}

const AssistantPanel = ({
    exchanges,
    asking,
    askFailed,
    conversations,
    activeThreadId,
    onAsk,
    onNewConversation,
    onOpenHistory,
    onOpenThread,
    onFeedback,
    onClose,
}: AssistantPanelProps) => {
    const { t, i18n } = useTranslation('assistant')
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
    const [question, setQuestion] = useState('')
    const [view, setView] = useState<'chat' | 'history'>('chat')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (view === 'chat') messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [exchanges, asking, view])

    const openHistory = () => {
        onOpenHistory()
        setView('history')
    }

    const formatThreadDate = (value: string) => {
        try {
            return new Date(value).toLocaleString(i18n.language, {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            })
        } catch {
            return ''
        }
    }

    const submit = () => {
        if (!question.trim() || asking) return
        onAsk(question)
        setQuestion('')
    }

    const panelPosition: React.CSSProperties = fullScreen
        ? { inset: 0, borderRadius: 0 }
        : {
              bottom: 96,
              right: 24,
              width: '40vw',
              minWidth: 380,
              maxWidth: 'calc(100vw - 48px)',
              height: '80vh',
              maxHeight: 'calc(100vh - 120px)',
              borderRadius: 12,
          }

    return (
        <Paper
            elevation={8}
            style={panelPosition}
            sx={{
                position: 'fixed',
                zIndex: theme.zIndex.modal,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                }}
            >
                {view === 'history' && (
                    <Tooltip title={t('back')}>
                        <IconButton size="small" color="inherit" onClick={() => setView('chat')}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2, color: "white" }}>
                        {view === 'history' ? t('history') : t('title')}
                    </Typography>
                    {view === 'chat' && (
                        <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
                            {t('subtitle')}
                        </Typography>
                    )}
                </Box>
                {view === 'chat' && (
                    <>
                        <Tooltip title={t('history')}>
                            <IconButton size="small" color="inherit" onClick={openHistory}>
                                <HistoryOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('newConversation')}>
                            <IconButton size="small" color="inherit" onClick={onNewConversation}>
                                <AddCommentOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Tooltip title={t('close')}>
                    <IconButton size="small" color="inherit" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {view === 'history' && (
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1.5, bgcolor: 'grey.50' }}>
                    {!conversations?.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, px: 0.5 }}>
                            {t('historyEmpty')}
                        </Typography>
                    ) : (
                        conversations.map((thread) => (
                            <Paper
                                key={thread.AssistantThreadId}
                                variant="outlined"
                                onClick={() => {
                                    onOpenThread(thread)
                                    setView('chat')
                                }}
                                sx={{
                                    p: 1.5,
                                    mb: 1,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    borderColor:
                                        thread.AssistantThreadId === activeThreadId
                                            ? 'primary.main'
                                            : 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                }}
                            >
                                <ChatBubbleOutlineIcon fontSize="small" color="action" />
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                        {thread.Title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatThreadDate(thread.UpdatedAt)}
                                        {' · '}
                                        {t('historyMessages', {
                                            count: thread.messages?.length ?? 0,
                                        })}
                                    </Typography>
                                </Box>
                            </Paper>
                        ))
                    )}
                </Box>
            )}

            {view === 'chat' && (
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
                {!exchanges.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {t('welcome')}
                    </Typography>
                )}
                {exchanges.map((exchange, index) => (
                    <Box key={exchange.id ?? `pending-${index}`} sx={{ mb: 2 }}>
                        {/* Automatic continuation: the question was re-sent by the
                            widget, not typed again — only show the answer */}
                        {!exchange.auto && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                <Box
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: 'primary.contrastText',
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: 2,
                                        borderBottomRightRadius: 4,
                                        maxWidth: '85%',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    <Typography variant="body2">{exchange.question}</Typography>
                                </Box>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Box
                                sx={{
                                    bgcolor: 'background.paper',
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    borderBottomLeftRadius: 4,
                                    maxWidth: '85%',
                                    boxShadow: 1,
                                    wordBreak: 'break-word',
                                }}
                            >
                                {exchange.pending ? (
                                    <TypingIndicator />
                                ) : (
                                    <>
                                        <Typography variant="body2" component="div">
                                            {renderAssistantMarkdown(exchange.answer)}
                                        </Typography>
                                        {!!exchange.sources?.length && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontWeight: 600 }}
                                                >
                                                    {t('sources')}
                                                </Typography>
                                                {exchange.sources.map((source) => (
                                                    <Box
                                                        key={source.url}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <ArticleOutlinedIcon
                                                            sx={{ fontSize: 14 }}
                                                            color="action"
                                                        />
                                                        <Link
                                                            href={source.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="caption"
                                                        >
                                                            {source.title}
                                                        </Link>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {!!exchange.id && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                    gap: 0.5,
                                                    mt: 0.5,
                                                }}
                                            >
                                                <Tooltip title={t('feedback.helpful')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onFeedback(exchange.id as number, true)
                                                        }
                                                    >
                                                        {exchange.liked === true ? (
                                                            <ThumbUpIcon
                                                                sx={{ fontSize: 14 }}
                                                                color="primary"
                                                            />
                                                        ) : (
                                                            <ThumbUpOutlinedIcon
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={t('feedback.notHelpful')}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            onFeedback(exchange.id as number, false)
                                                        }
                                                    >
                                                        {exchange.liked === false ? (
                                                            <ThumbDownIcon
                                                                sx={{ fontSize: 14 }}
                                                                color="error"
                                                            />
                                                        ) : (
                                                            <ThumbDownOutlinedIcon
                                                                sx={{ fontSize: 14 }}
                                                            />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ))}
                {askFailed && !asking && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {t('error')}
                    </Alert>
                )}
                <div ref={messagesEndRef} />
            </Box>
            )}

            {view === 'chat' && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 1,
                    p: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                }}
            >
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    size="small"
                    value={question}
                    placeholder={t('inputPlaceholder')}
                    onChange={(event) => setQuestion(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault()
                            submit()
                        }
                    }}
                    inputProps={{ maxLength: 2000 }}
                />
                <Tooltip title={t('send')}>
                    <span>
                        <IconButton
                            color="primary"
                            onClick={submit}
                            disabled={asking || !question.trim()}
                        >
                            <SendIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            )}
        </Paper>
    )
}

export default AssistantPanel
